import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { RecordDetail } from "@/components/RecordDetail";
import { ApplicationDetail } from "@/components/ApplicationDetail";
import { PageHeader } from "@/components/PageHeader";
import { inr } from "@/lib/format";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const APP_STATUSES = ["Submitted", "Login", "Under Review", "Bank Submitted", "Approved", "Sanctioned", "Rejected", "Disbursed", "Hold"];
const LIST_VISIBLE = ["Rejected", "Bank Submitted", "Disbursed"];
const PAGE_SIZE = 25;

export default function Applications() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState(null);
  const [statusEdit, setStatusEdit] = useState(null); // {app, status, feedback}

  const { data: apps = [], isLoading } = useQuery({ queryKey: ["applications"], queryFn: async () => (await api.get("/applications")).data });

  const mutate = useMutation({
    mutationFn: async ({ id, status, feedback }) => (await api.patch(`/applications/${id}/status`, { status, feedback })).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      setStatusEdit(null);
      if (!LIST_VISIBLE.includes(vars.status)) {
        toast.info(`Status set to "${vars.status}". This stage isn't shown in the main list view — it stays saved and visible in the mobile app.`, { duration: 6000 });
      } else {
        toast.success("Application status updated");
      }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const filtered = useMemo(() => {
    let list = apps;
    if (statusFilter !== "all") list = list.filter((a) => a.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((a) => [a.applicant_name, a.lead_name, a.bank_name, a.loan_type, a.reference_number].some((v) => String(v || "").toLowerCase().includes(q)));
    return list;
  }, [apps, search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div data-testid="applications-page">
      <PageHeader title="Loan Applications" subtitle={`${filtered.length} applications`} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="applications-search-input" placeholder="Search applicant, bank, ref…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-white dark:bg-slate-950" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-full sm:w-48 bg-white dark:bg-slate-950" data-testid="applications-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="applications-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Applicant</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Bank</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Loan Type</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Amount</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Ref No.</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 8 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && pageItems.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No applications found.</TableCell></TableRow>}
            {!isLoading && pageItems.map((app) => (
              <TableRow key={app.id} className="cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-800/40" data-testid={`application-row-${app.id}`} onClick={() => setDetail(app)}>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{app.applicant_name || app.lead_name || "—"}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{app.bank_name || "—"}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{app.loan_type || "—"}</TableCell>
                <TableCell className="text-right font-num font-semibold">{inr(app.loan_amount)}</TableCell>
                <TableCell className="font-num text-xs text-slate-500">{app.reference_number || "—"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`application-status-btn-${app.id}`} onClick={() => setStatusEdit({ app, status: app.status, feedback: "" })}><StatusBadge status={app.status} className="cursor-pointer hover:opacity-80" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-500">Page {page + 1} of {pageCount}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="apps-prev-page"><ChevronLeft className="h-4 w-4" /> Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} data-testid="apps-next-page">Next <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <ApplicationDetail app={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />

      <Dialog open={!!statusEdit} onOpenChange={(o) => !o && setStatusEdit(null)}>
        <DialogContent className="bg-white dark:bg-slate-900" data-testid="application-status-dialog">
          <DialogHeader><DialogTitle className="font-heading">Update Application Status</DialogTitle><DialogDescription className="sr-only">Change the status of this loan application</DialogDescription></DialogHeader>
          {statusEdit && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusEdit.status} onValueChange={(v) => setStatusEdit({ ...statusEdit, status: v })}>
                  <SelectTrigger data-testid="application-status-dialog-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Feedback / Note</Label>
                <Textarea value={statusEdit.feedback} onChange={(e) => setStatusEdit({ ...statusEdit, feedback: e.target.value })} placeholder="Optional note for this status change" data-testid="application-status-feedback" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusEdit(null)}>Cancel</Button>
            <Button className="bg-blue-700 hover:bg-blue-800 text-white" disabled={mutate.isPending} onClick={() => mutate.mutate({ id: statusEdit.app.id, status: statusEdit.status, feedback: statusEdit.feedback })} data-testid="application-status-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
